import { parse } from './contracts.mjs';
import { InspectionError, requireCondition } from './errors.mjs';
import {
  InspectBinding,
  parseInspectRequest,
  parseInspectReceipt,
  NativeInspectBootstrap,
  InspectResolveResult,
  InspectPreparedResult,
  InspectIntentResult,
  InspectFrozenResult,
} from './host-contracts.mjs';
import { createInspectManifestView } from './manifest-view.mjs';
import { createNativeInspectMap } from './native-map.mjs';

// Browser safe: no repository, node:crypto, model client or second controller.
export function createNativeInspectClient({ binding: raw, fetch: send = globalThis.fetch }) {
  const binding = Object.freeze(parse(InspectBinding, raw));
  if (typeof send !== 'function') throw new TypeError('fetch is required');
  const prefix = `/api/v1/company/artifacts/${encodeURIComponent(binding.artifactId)}/inspect/`;
  async function request(operation, input) {
    const body = parseInspectRequest(operation, { binding, input });
    const response = await send(`${prefix}${operation}`, {
      method: 'POST',
      credentials: 'same-origin',
      mode: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok) {
      const code =
        typeof result?.error === 'string' && /^[a-z_]{1,120}$/u.test(result.error)
          ? result.error
          : 'native_inspect_unavailable';
      throw new InspectionError(code);
    }
    return result;
  }
  async function bootstrap() {
    const result = parse(NativeInspectBootstrap, await request('bootstrap', {}));
    const expected = { ...binding, manifestDigest: result.manifest.manifestDigest };
    const view = createInspectManifestView(result.manifest, expected);
    const nativeMap = createNativeInspectMap(result.mapping, expected);
    requireCondition(
      result.manifest.targets.length === result.mapping.targets.length &&
        result.mapping.targets.every((target) => view.targetIdFor(target.pid) === target.targetId),
      'manifest_binding_mismatch',
    );
    return Object.freeze({ ...result, view, nativeMap });
  }
  async function commit(operation, input) {
    const ticket =
      operation === 'apply'
        ? {
            ...binding,
            action: 'apply',
            operationId: input.operationId,
            operationDigest: input.operationDigest,
          }
        : {
            ...binding,
            action: 'undo',
            operationId: input.operationId,
            expectedRevisionId: input.expectedRevisionId,
            idempotencyKey: input.idempotencyKey,
          };
    return parseInspectReceipt(await request(operation, input), ticket);
  }
  return Object.freeze({
    bootstrap,
    api: Object.freeze({
      resolve: async (input) => parse(InspectResolveResult, await request('resolve', input)),
      freeze: async (input) => parse(InspectFrozenResult, await request('freeze', input)),
      prepare: async (input) => parse(InspectPreparedResult, await request('prepare', input)),
      apply: (input) => commit('apply', input),
      undo: (input) => commit('undo', input),
    }),
    guard: Object.freeze({
      acquire: (input) => {
        const requested = parse(InspectBinding, input);
        requireCondition(
          Object.keys(binding).every((key) => requested[key] === binding[key]),
          'inspect_binding_mismatch',
        );
        return request('acquire', {});
      },
      release: async (lease) => {
        await request('release', lease);
      },
    }),
    issueHostIntent: async (ticket) =>
      parse(InspectIntentResult, await request('intent', ticket)).hostIntent,
    readCommitReceipt: async (ticket) =>
      parseInspectReceipt(await request('receipt', ticket), ticket),
    originalUndo: async (rawTicket, rawInput) => {
      const ticket = parseInspectRequest('receipt', { binding, input: rawTicket }).input;
      const input = parseInspectRequest('undo', { binding, input: rawInput }).input;
      requireCondition(
        ticket.action === 'undo' &&
          ticket.operationId === input.operationId &&
          ticket.expectedRevisionId === input.expectedRevisionId &&
          ticket.idempotencyKey === input.idempotencyKey,
        'inspect_binding_mismatch',
      );
      return commit('undo', input);
    },
  });
}
